import React, { useEffect, useState } from 'react';
import { HostInviteService, IncomingInvite } from 'mesh-microkernel';
import { MasterEnrollmentIssuer } from '../mesh/enrollment/master-enrollment-issuer.js';

interface InvitePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  inviteService: HostInviteService;
  targetNodeId?: string;
  defaultChannelUri?: string;
  onGrantCollaborator?: (targetNodeId: string) => void;
}

export const InvitePopover: React.FC<InvitePopoverProps> = ({
  isOpen,
  onClose,
  inviteService,
  targetNodeId = '',
  defaultChannelUri = 'hts://mesh/chat/general',
  onGrantCollaborator,
}) => {
  const [targetId, setTargetId] = useState(targetNodeId);
  const [channelUri, setChannelUri] = useState(defaultChannelUri);
  const [incomingInvites, setIncomingInvites] = useState<IncomingInvite[]>([]);
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    setTargetId(targetNodeId);
  }, [targetNodeId]);

  useEffect(() => {
    const unsub = inviteService.onInviteReceived((invite) => {
      setIncomingInvites((prev) => [...prev, invite]);
    });
    return () => unsub();
  }, [inviteService]);

  if (!isOpen) return null;

  const handleSendInvite = async () => {
    if (!targetId || !channelUri) return;
    setStatusMsg('Sending invite...');
    const res = await inviteService.sendInvite({
      targetNodeId: targetId,
      channelUri,
    });

    if (onGrantCollaborator && targetId) {
      onGrantCollaborator(targetId);
    }

    if (res.ok) {
      setStatusMsg('Invite sent successfully!');
      setTimeout(() => setStatusMsg(''), 3000);
    } else {
      setStatusMsg(`Error: ${(res as any).error?.message || 'Failed to send invite'}`);
    }
  };

  const handleAccept = async (inviteId: string) => {
    const res = await inviteService.acceptInvite(inviteId);
    if (res.ok) {
      setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
      setStatusMsg(`Joined channel: ${res.value.channelUri}`);
    }
  };

  const handleReject = async (inviteId: string) => {
    await inviteService.rejectInvite(inviteId);
    setIncomingInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
  };

  const handleGenerateHelperLink = () => {
    const link = MasterEnrollmentIssuer.generateDeepLink({
      clusterId: 'uganda-mesh-01',
      masterNodeId: 'sovereign-master', // Typically from auth context
      masterSpkiFingerprint: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855', // Usually dynamic
      roles: ['compute', 'relay'],
      maxRamGb: 8,
      maxCpuPercent: 75,
      maxStorageGb: 40,
      ttlSeconds: 86400,
    });
    navigator.clipboard.writeText(link);
    setStatusMsg('Helper Node link copied to clipboard!');
    setTimeout(() => setStatusMsg(''), 3000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--app-header-height, 48px) + 8px)',
        right: '16px',
        width: '340px',
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        zIndex: 110,
        padding: '16px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '14px', color: '#f8fafc' }}>✉️ Mesh Invitations</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>✕</button>
      </div>

      {/* Send Invite Form */}
      <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <label style={{ fontSize: '11px', color: '#94a3b8' }}>Target Node ID:</label>
        <input
          id="target-node-id-input"
          type="text"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder="peer-beta"
          style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: 'white', fontSize: '12px' }}
        />

        <label style={{ fontSize: '11px', color: '#94a3b8' }}>Channel URI:</label>
        <input
          type="text"
          value={channelUri}
          onChange={(e) => setChannelUri(e.target.value)}
          placeholder="hts://mesh/chat/general"
          style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: 'white', fontSize: '12px' }}
        />

        <button
          id="send-invite-btn"
          onClick={handleSendInvite}
          style={{ padding: '8px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '12px', marginTop: '4px' }}
        >
          Send Invite
        </button>
      </div>

      {statusMsg && <div style={{ fontSize: '11px', color: '#60a5fa', marginBottom: '12px' }}>{statusMsg}</div>}

      {/* Incoming Invites */}
      <div>
        <h4 style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
          Incoming Invitations ({incomingInvites.length})
        </h4>
        {incomingInvites.map((inv) => (
          <div key={inv.inviteId} style={{ padding: '8px', backgroundColor: '#0f172a', borderRadius: '6px', marginBottom: '6px' }}>
            <div style={{ fontSize: '12px', color: '#f1f5f9' }}>From: {inv.senderNodeId}</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>{inv.channelUri}</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => handleAccept(inv.inviteId)} style={{ flex: 1, padding: '4px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Accept</button>
              <button onClick={() => handleReject(inv.inviteId)} style={{ flex: 1, padding: '4px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Reject</button>
            </div>
          </div>
        ))}
      </div>

      {/* Helper Node Enrollment (HET-01B) */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #334155' }}>
        <h4 style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
          Helper Node
        </h4>
        <button
          onClick={handleGenerateHelperLink}
          style={{ width: '100%', padding: '8px', backgroundColor: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
        >
          Generate Helper Link
        </button>
      </div>
    </div>
  );
};
